import { useState, useCallback } from 'react';
import { Link, useSearchParams, useFetcher } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Mail, AlertTriangle } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '~/components/ui/form';
import { Spinner } from '~/components/ui/spinner';

// Some students get their magic link handed to them directly (when the
// owner-only Resend send is skipped). Let them paste it here to skip the
// "open mail, click link" step — we just navigate to /auth/verify with
// whatever token is in the URL they paste.
function PasteLinkPanel() {
  const [link, setLink] = useState('');
  const [err, setErr] = useState('');

  const openLink = () => {
    setErr('');
    const trimmed = link.trim();
    if (!trimmed) {
      setErr('Paste the full link first.');
      return;
    }
    try {
      const url = new URL(trimmed, window.location.origin);
      // Only allow same-origin /auth/verify URLs with a token query param.
      if (url.origin !== window.location.origin) {
        setErr("That link isn't for this site.");
        return;
      }
      if (!url.pathname.startsWith('/auth/verify') || !url.searchParams.get('token')) {
        setErr("That doesn't look like a valid magic link.");
        return;
      }
      window.location.href = url.pathname + url.search;
    } catch {
      setErr("That doesn't look like a valid URL.");
    }
  };

  return (
    <div className="mt-6 rounded-lg border border-border-subtle bg-bg-elevated px-4 py-3">
      <p className="font-body text-sm text-text-primary mb-1">
        Got a link from the team?
      </p>
      <p className="font-body text-xs text-text-muted mb-2">
        Paste it below and we&apos;ll log you in.
      </p>
      <input
        type="text"
        value={link}
        onChange={(e) => setLink(e.target.value)}
        placeholder="https://.../auth/verify?token=..."
        className="w-full bg-bg-primary border border-border-subtle rounded-md px-3 py-2 font-mono text-xs text-text-primary focus:outline-none focus:border-accent-cyan mb-2"
      />
      {err && <p className="font-body text-xs text-error mb-2">{err}</p>}
      <button
        type="button"
        onClick={openLink}
        className="font-heading text-sm font-medium bg-accent-cyan text-bg-primary px-4 py-2 rounded-md hover:bg-accent-hover transition-colors w-full"
      >
        Sign in with link
      </button>
    </div>
  );
}

const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type FormValues = z.infer<typeof schema>;

function AlertBanner({ variant, message }: { variant: 'warning' | 'error'; message: string }) {
  const styles = {
    warning: { border: 'border-warning/20', bg: 'bg-warning-dim', icon: 'text-warning' },
    error: { border: 'border-error/20', bg: 'bg-error-dim', icon: 'text-error' },
  }[variant];
  return (
    <div className={`mb-6 flex items-start gap-3 rounded-lg border ${styles.border} ${styles.bg} px-4 py-3`}>
      <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${styles.icon}`} aria-hidden="true" />
      <p className="font-body text-sm text-text-secondary">{message}</p>
    </div>
  );
}

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const fetcher = useFetcher();
  const [hasReset, setHasReset] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [showLogin, setShowLogin] = useState(false);

  const isInvalidated = searchParams.get('reason') === 'invalidated';
  const isInvalidToken = searchParams.get('reason') === 'invalid_token';
  const isServerError = searchParams.get('reason') === 'server_error';

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  // hasReset is set to false before each submit and true on "Try again".
  // Because React 18 batches both state updates, showSent goes false→true→false
  // atomically — the "Check your email" panel never flickers mid-transition.
  const showSent = fetcher.state === 'idle' && fetcher.data?.success === true && !hasReset;
  const isLoading = fetcher.state === 'submitting';

  // R2: memoized onSubmit
  const onSubmit = useCallback((values: FormValues) => {
    setHasReset(false);
    setSubmittedEmail(values.email);
    fetcher.submit(
      { email: values.email },
      { method: 'post', action: '/api/auth/request-magic-link' }
    );
  }, [fetcher]);

  const resetForm = () => {
    setHasReset(true);
    form.reset();
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center pt-16 px-4 sm:px-6">
      <div className="w-full max-w-6xl flex flex-col lg:flex-row items-stretch gap-10 lg:gap-16">
        <div className="hidden lg:flex lg:flex-[1.4] relative items-center">
          <div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              background:
                'radial-gradient(circle at 30% 30%, rgba(34,211,238,0.18) 0%, transparent 60%)',
            }}
          />
          <img
            src="/course-thumbnail.webp"
            alt=""
            className="relative w-full h-[520px] rounded-2xl object-cover border border-border-subtle shadow-card-hover"
          />
        </div>
        <div className="w-full lg:flex-1 lg:max-w-md flex flex-col justify-center">
        {isInvalidated && !showSent && (
          <AlertBanner
            variant="warning"
            message="You've been logged out because your account was accessed from another device."
          />
        )}
        {isInvalidToken && !showSent && (
          <AlertBanner
            variant="error"
            message="This link is invalid or expired. Request a new one."
          />
        )}
        {isServerError && !showSent && (
          <AlertBanner
            variant="error"
            message="Something went wrong. Please try again."
          />
        )}

        {showSent ? (
          <div className="text-center">
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-accent-dim">
              <Mail className="h-7 w-7 text-accent-cyan" />
            </div>
            <h1 className="font-heading text-h2 text-text-primary mb-3">
              Check your email
            </h1>
            <p className="font-body text-body text-text-secondary mb-2">
              We sent a link to{' '}
              <span className="text-text-primary font-medium">{submittedEmail}</span>
            </p>
            <p className="font-body text-sm text-text-muted mb-6">
              Click the link to sign in. It expires in 24 hours.
            </p>
            <div className="mb-8 rounded-lg border border-border-subtle bg-bg-elevated px-4 py-3 text-left space-y-2">
              <p className="font-body text-sm text-text-secondary">
                <span className="text-text-primary font-medium">No email?</span> Check your spam folder or whitelist{' '}
                <span className="font-mono text-xs text-text-primary">farealahmed@gmail.com</span>.
              </p>
              <p className="font-body text-sm text-text-secondary">
                Still nothing?{' '}
                <a
                  href="mailto:farealahmed@gmail.com"
                  className="text-accent-cyan hover:text-accent-hover underline underline-offset-2 transition-colors"
                >
                  Email me
                </a>{' '}
                and I'll get you access directly.
              </p>
            </div>
            <button
              onClick={resetForm}
              className="font-body text-sm text-accent-cyan hover:text-accent-hover underline underline-offset-2 transition-colors"
            >
              Try again
            </button>
            <PasteLinkPanel />
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h1 className="font-heading text-h2 text-text-primary mb-2">
                Welcome back
              </h1>
              <p className="font-body text-sm text-text-secondary">
                {showLogin
                  ? 'Enter your email to receive a magic link.'
                  : 'Explore the LMS with sample data, or sign in to your account.'}
              </p>
            </div>

            {showLogin ? (
              <>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-text-secondary">Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="you@example.com"
                              disabled={isLoading}
                              className="bg-bg-surface border-border-subtle text-text-primary placeholder:text-text-muted focus-visible:border-accent-cyan focus-visible:ring-accent-cyan/20"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-error" />
                        </FormItem>
                      )}
                    />

                    {fetcher.data?.error === 'rate_limited' && (
                      <p className="font-body text-sm text-error">
                        Too many attempts. Please wait a few minutes.
                      </p>
                    )}
                    {fetcher.data?.error === 'server_error' && (
                      <p className="font-body text-sm text-error">
                        Something went wrong. Please try again.
                      </p>
                    )}

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-accent-cyan text-bg-primary hover:bg-accent-hover hover:shadow-glow transition-all duration-200"
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Spinner className="text-bg-primary" />
                          Sending...
                        </span>
                      ) : (
                        'Send Magic Link'
                      )}
                    </Button>
                  </form>
                </Form>

                <PasteLinkPanel />

                <button
                  type="button"
                  onClick={() => setShowLogin(false)}
                  className="mt-6 w-full text-center font-body text-sm text-text-secondary hover:text-text-primary transition-colors"
                >
                  ← Back
                </button>
              </>
            ) : (
              <>
                <div className="rounded-lg border border-border-subtle bg-bg-elevated px-4 py-4 text-center">
                  <p className="font-body text-sm text-text-primary mb-1">
                    Just looking around?
                  </p>
                  <p className="font-body text-xs text-text-muted mb-3">
                    Skip the email step and explore the LMS with sample data.
                  </p>
                  <form method="post" action="/api/auth/guest-tour">
                    <button
                      type="submit"
                      className="font-heading text-sm font-medium border border-accent-cyan text-accent-cyan px-4 py-2 rounded-md hover:bg-accent-cyan hover:text-bg-primary transition-colors w-full"
                    >
                      {/* Try as guest */}
                      Try as student
                    </button>
                  </form>
                  <form method="post" action="/api/auth/instructor-tour" className="mt-2">
                    <button
                      type="submit"
                      className="font-heading text-sm font-medium border border-accent-cyan text-accent-cyan px-4 py-2 rounded-md hover:bg-accent-cyan hover:text-bg-primary transition-colors w-full"
                    >
                      Try as instructor
                    </button>
                  </form>
                </div>

                <button
                  type="button"
                  onClick={() => setShowLogin(true)}
                  className="mt-6 w-full text-center font-body text-sm text-accent-cyan hover:text-accent-hover transition-colors"
                >
                  Login with your account →
                </button>

                <p className="mt-6 text-center font-body text-sm text-text-secondary">
                  New here?{' '}
                  <Link
                    to="/"
                    className="text-accent-cyan hover:text-accent-hover underline underline-offset-2 transition-colors"
                  >
                    Learn about the course →
                  </Link>
                </p>
              </>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
}
